import { computed, ref } from 'vue'
import axios from '@/lib/API/client'
import router from '@/router'
import store from '@/store'
import {
    INTERACTIVE_TUTORIAL_STAGES,
    INTERACTIVE_TUTORIAL_STEPS,
    isInteractiveTutorialActive,
    isProcessedMoveProSession,
    normalizeInteractiveTutorialStage,
} from '@/lib/interactive-tutorial/stages'

const saving = ref(false)
const saveError = ref(null)
const pendingMoveProSessionId = ref(null)
let pendingMoveProUserId = null
// The synced session the guide opened, until the rider validates it: the
// account is still at the sync stage, but syncing is already done.
const awaitingValidationSessionId = ref(null)
const awaitingValidationUserId = ref(null)
let writeQueue = Promise.resolve()

const currentUser = () => store.state.auth?.user
const currentStage = () => normalizeInteractiveTutorialStage(currentUser()?.interactive_tutorial_stage)
const isCurrentUser = (userId) => Boolean(userId) && String(currentUser()?.id) === String(userId)

export const getInteractiveTutorialPrincipalId = () => currentUser()?.id ?? null

const commitUser = (user) => store.dispatch('auth/setUser', user)
const clearPendingMoveProSession = () => {
    pendingMoveProSessionId.value = null
    pendingMoveProUserId = null
}
const clearAwaitingValidation = () => {
    awaitingValidationSessionId.value = null
    awaitingValidationUserId.value = null
}

const writeStage = (nextStage) => {
    const requestedUserId = currentUser()?.id
    const normalized = normalizeInteractiveTutorialStage(nextStage)
    if (normalized !== nextStage) {
        return Promise.reject(new Error(`Unknown interactive tutorial stage: ${nextStage}`))
    }

    const update = async () => {
        const previousUser = currentUser()
        if (!requestedUserId || String(previousUser?.id) !== String(requestedUserId)) return false
        if (currentStage() === normalized) return true

        if (normalized === INTERACTIVE_TUTORIAL_STAGES.SYNC) {
            clearPendingMoveProSession()
        } else if (![INTERACTIVE_TUTORIAL_STAGES.SYNC, INTERACTIVE_TUTORIAL_STAGES.RESULTS].includes(normalized)) {
            clearPendingMoveProSession()
        }

        const userId = previousUser.id
        saveError.value = null
        saving.value = true
        await commitUser({ ...previousUser, interactive_tutorial_stage: normalized })

        try {
            const response = await axios.put('/user', { interactive_tutorial_stage: normalized })
            if (!isCurrentUser(userId)) return false

            const responseUser = response?.data && typeof response.data === 'object' ? response.data : {}
            const persistedStage = normalizeInteractiveTutorialStage(responseUser.interactive_tutorial_stage)
            await commitUser({
                ...currentUser(),
                ...responseUser,
                // Safety rule: an old serializer that omits the field must not
                // accidentally enroll an existing account.
                interactive_tutorial_stage: persistedStage,
            })
            if (persistedStage === normalized) clearAwaitingValidation()
            return persistedStage === normalized
        } catch (error) {
            if (isCurrentUser(userId)) {
                if (currentUser()?.interactive_tutorial_stage === normalized) {
                    await commitUser(previousUser)
                }
                saveError.value = error
            }
            throw error
        } finally {
            saving.value = false
        }
    }

    const queued = writeQueue.then(update, update)
    writeQueue = queued.catch(() => undefined)
    return queued
}

export const notifyMoveProSessionUploaded = async (sessionId, uploadingUserId) => {
    const normalizedId = Number(sessionId)
    const userId = currentUser()?.id
    if (
        !uploadingUserId ||
        String(userId) !== String(uploadingUserId) ||
        currentStage() !== INTERACTIVE_TUTORIAL_STAGES.SYNC ||
        !Number.isFinite(normalizedId) ||
        normalizedId <= 0
    ) {
        return false
    }

    if (!userId) return false
    if (pendingMoveProUserId && String(pendingMoveProUserId) !== String(userId)) clearPendingMoveProSession()
    pendingMoveProSessionId.value = normalizedId
    pendingMoveProUserId = userId
    return true
}

export const openPendingMoveProTutorialSession = async () => {
    const sessionId = pendingMoveProSessionId.value
    const userId = currentUser()?.id
    if (
        ![INTERACTIVE_TUTORIAL_STAGES.SYNC, INTERACTIVE_TUTORIAL_STAGES.RESULTS].includes(currentStage()) ||
        !sessionId ||
        !userId ||
        String(pendingMoveProUserId) !== String(userId)
    ) {
        return false
    }

    try {
        await router.push({ name: 'sessions.session', params: { sessionId } })
        if (
            isCurrentUser(userId) &&
            String(pendingMoveProUserId) === String(userId) &&
            String(pendingMoveProSessionId.value) === String(sessionId)
        ) {
            clearPendingMoveProSession()
            if (currentStage() === INTERACTIVE_TUTORIAL_STAGES.SYNC) {
                awaitingValidationSessionId.value = Number(sessionId)
                awaitingValidationUserId.value = userId
            }
        }
        return true
    } catch {
        return false
    }
}

// The session the guide waits for can no longer be validated (failed, gone):
// the next step goes back to syncing.
export const dropAwaitingValidationSession = (sessionId) => {
    if (String(awaitingValidationSessionId.value) === String(sessionId)) clearAwaitingValidation()
}

export const openAwaitingValidationSession = async () => {
    const sessionId = awaitingValidationSessionId.value
    const userId = currentUser()?.id
    if (
        currentStage() !== INTERACTIVE_TUTORIAL_STAGES.SYNC ||
        !sessionId ||
        !userId ||
        String(awaitingValidationUserId.value) !== String(userId)
    ) {
        return false
    }

    try {
        await router.push({ name: 'sessions.session', params: { sessionId } })
        return true
    } catch {
        return false
    }
}

export default () => {
    const stage = computed(() => currentStage())
    const active = computed(() => isInteractiveTutorialActive(stage.value))
    const stepIndex = computed(() => INTERACTIVE_TUTORIAL_STEPS.indexOf(stage.value))
    const currentPendingMoveProSessionId = computed(() =>
        pendingMoveProUserId && String(pendingMoveProUserId) === String(currentUser()?.id)
            ? pendingMoveProSessionId.value
            : null
    )
    const currentAwaitingValidationSessionId = computed(() =>
        stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC &&
        awaitingValidationUserId.value &&
        String(awaitingValidationUserId.value) === String(currentUser()?.id)
            ? awaitingValidationSessionId.value
            : null
    )

    const markResultsReady = (session) => {
        if (
            stage.value !== INTERACTIVE_TUTORIAL_STAGES.SYNC ||
            !isProcessedMoveProSession(session) ||
            !session?.validated
        ) {
            return Promise.resolve(false)
        }

        const userId = currentUser()?.id
        // Not isSessionOwner on purpose: that helper also rejects an empty userId, which this check never did.
        const ownsSession = Array.isArray(session?.owners)
            ? session.owners.some((owner) => String(owner?.id) === String(userId))
            : false
        if (!ownsSession) return Promise.resolve(false)

        if (pendingMoveProUserId && String(pendingMoveProUserId) !== String(userId)) clearPendingMoveProSession()
        if (pendingMoveProSessionId.value && String(pendingMoveProSessionId.value) !== String(session.id)) {
            return Promise.resolve(false)
        }
        if (!pendingMoveProSessionId.value && session?.canEdit !== true) return Promise.resolve(false)

        pendingMoveProSessionId.value = Number(session.id)
        pendingMoveProUserId = userId
        return writeStage(INTERACTIVE_TUTORIAL_STAGES.RESULTS)
    }

    return {
        stage,
        active,
        stepIndex,
        stepCount: INTERACTIVE_TUTORIAL_STEPS.length,
        pendingMoveProSessionId: currentPendingMoveProSessionId,
        awaitingValidationSessionId: currentAwaitingValidationSessionId,
        saving,
        saveError,
        start: () => writeStage(INTERACTIVE_TUTORIAL_STAGES.HORSE),
        quit: () => writeStage(INTERACTIVE_TUTORIAL_STAGES.SKIPPED),
        complete: () => writeStage(INTERACTIVE_TUTORIAL_STAGES.COMPLETED),
        horseCreated: (creatingUserId) =>
            isCurrentUser(creatingUserId) && stage.value === INTERACTIVE_TUTORIAL_STAGES.HORSE
                ? writeStage(INTERACTIVE_TUTORIAL_STAGES.RECORD)
                : Promise.resolve(false),
        useExistingHorse: () =>
            stage.value === INTERACTIVE_TUTORIAL_STAGES.HORSE
                ? writeStage(INTERACTIVE_TUTORIAL_STAGES.RECORD)
                : Promise.resolve(false),
        moveProRideStopped: () =>
            stage.value === INTERACTIVE_TUTORIAL_STAGES.RECORD
                ? writeStage(INTERACTIVE_TUTORIAL_STAGES.SYNC)
                : Promise.resolve(false),
        moveProSessionReadyToSync: () =>
            stage.value === INTERACTIVE_TUTORIAL_STAGES.RECORD
                ? writeStage(INTERACTIVE_TUTORIAL_STAGES.SYNC)
                : Promise.resolve(false),
        markResultsReady,
    }
}
